import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/storage';
import { AdminMessage } from '../models/admin-message';
import { WalletAccount } from '../models/wallet-account';
import { InteracEmail } from '../models/interac-email';
import { IssueReport } from '../models/issue-report';
import { DepositIssueReport } from '../models/deposit-issue-report';
import { Beneficiary } from '../models/beneficiary';
import { allTransaction, Transaction } from '../models/transaction';
import { User } from '../models/user';
import { Post } from '../models/post';


/** One card of the privacy policy, as the mobile app renders it.
 *  `icon` is a key from a closed list the app resolves to a Material icon —
 *  never a raw code point, which would break Flutter's icon tree-shaking. */
export interface PrivacySection {
  icon: string;
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
}

/** One threshold pair for a single flow in a single currency. 0 = disabled. */
export interface LimitPair {
  reviewAbove: number;
  blockAbove: number;
}

/** Absolute bounds on the rate a user may type on a marketplace post.
 *  Expressed in the one direction the app always stores: XAF per 1 CAD.
 *  0 = disabled, like every threshold in config/limits. */
export interface RateBound {
  min: number;
  max: number;
}

/** One direction of the corridor. `disabled: true` suspends it. */
export interface CountryFlags {
  /** The country at all. Opt-in: anything but exactly true closes it. */
  enabled: boolean;
  /** Direct transfers. Opt-out: absent means on once `enabled` is true. */
  directEnabled: boolean;
  /** Marketplace posts and offers. Opt-out, same as above. */
  marketplaceEnabled: boolean;
  /** Shown to the customer when the country is closed. */
  messageFr: string;
  messageEn: string;
}

export interface DepositRailField {
  label: string;
  value: string;
}

export interface DepositRailConfig {
  enabled: boolean;
  bankName: string;
  accountName: string;
  /** Free-form: IBAN/BIC, sort code + account number and CNAPS share no schema. */
  fields: DepositRailField[];
  referenceHint: string;
  instructionsFr: string;
  instructionsEn: string;
  processingNoteFr: string;
  processingNoteEn: string;
}

export interface CorridorSide {
  disabled: boolean;
  messageFr: string;
  messageEn: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(private afs : AngularFirestore, private fireStorage : AngularFireStorage) { }


  // add user
  adduser(user : User) {
    user.id = this.afs.createId();
    return this.afs.collection('/users').add(user);
  }
  
  // get all users
  getAllusers() { 
    return this.afs.collection('/users').snapshotChanges();
  }

  // get Pay
  getPay() { 
    return this.afs.collection('/pay').valueChanges({idField: '"mg6GHY8RPYCo7KzHobR2"'});
  }

    // delete user
  deleteuser(user : User) {
      this.afs.doc('/users/'+user.id).delete();
  }

  // update user
  updateuser(user : User) {
    this.deleteuser(user);
    this.adduser(user);
  }

  // update pay
  updatePay(id: string, amount: number) {
    const data = {amount: amount};
    this.afs.collection('/pay').doc(id).update(data);
  }

  // ── Platform Fees (config/fees) ───────────────────────────────────────────
  // Three independent percentage fees charged to users, read by the Flutter
  // app from config/fees: post (post owner), offer (bidder), direct transfer.

  getFees() {
    return this.afs.collection('/config').doc('fees').valueChanges();
  }

  // Upsert the three percentages. merge:true so the doc is created on first
  // save and partial writes never wipe sibling fields.
  updateFees(fees: { postFeePercent: number; offerFeePercent: number; directTransferFeePercent: number }) {
    return this.afs.collection('/config').doc('fees').set(fees, { merge: true });
  }

  // ── Interac config (config/interac) ───────────────────────────────────────
  // The domain shown to CA customers as their dedicated receiving address
  // (`{alias}@{aliasDomain}`), read by the Flutter deposit page.

  getInteracConfig() {
    return this.afs.collection('/config').doc('interac').valueChanges();
  }

  updateInteracConfig(cfg: { aliasDomain: string }) {
    return this.afs.collection('/config').doc('interac').set(cfg, { merge: true });
  }

  // ── Mobile Money config (config/momo) ─────────────────────────────────────
  // The platform's receiving MTN + Orange numbers shown to CM customers on the
  // deposit page, read by the Flutter app from config/momo.

  getMomoConfig() {
    return this.afs.collection('/config').doc('momo').valueChanges();
  }

  updateMomoConfig(cfg: { mtnNumber: string; orangeNumber: string }) {
    return this.afs.collection('/config').doc('momo').set(cfg, { merge: true });
  }

  // ── Exchange rates (config/rates) ─────────────────────────────────────────
  // The CAD↔XAF rates shown to customers. `mode: 'auto'` leaves the apps on the
  // public exchangerate API (historical behaviour); `mode: 'manual'` makes the
  // two rates below authoritative — the mobile app then stops calling the API
  // and transferMoney recomputes the recipient amount from cadToXaf.
  // A rate of 0 or below counts as "not configured" everywhere and falls back
  // to auto, so a bad value can never zero out a recipient's amount.

  getRates() {
    return this.afs.collection('/config').doc('rates').valueChanges();
  }

  updateRates(rates: {
    mode: 'auto' | 'manual';
    cadToXaf: number;
    xafToCad: number;
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('rates').set(rates, { merge: true });
  }

  // ── Marketplace rate bounds (config/rateLimits) ───────────────────────────
  // The window a POST AUTHOR may type in, not a rate the platform applies.
  // Stored apart from config/rates on purpose: a bad value here refuses a post,
  // a bad value there zeroes out a recipient's payout — the two must never
  // share a write.
  //
  // One pair covers both post directions. The mobile app always stores a post
  // rate as "N of the WEAKER currency for 1 of the STRONGER" (see
  // AwesomeFormDialog: the map is built as '<typed><weaker>' : '1<stronger>'),
  // so on this corridor the number the user types is always XAF per 1 CAD,
  // whichever way the post points.
  //
  // 0 (or absent) disables a bound, so an unconfigured document changes nothing.

  getRateLimits() {
    return this.afs.collection('/config').doc('rateLimits').valueChanges();
  }

  updateRateLimits(cfg: {
    marketplace: { XAF_PER_CAD: RateBound };
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('rateLimits').set(cfg, { merge: true });
  }

  // ── Maintenance mode (config/maintenance) ─────────────────────────────────
  // The platform kill switch. When `isActive` is true the Flutter app and the
  // public site show the admin's message instead of their normal content, and
  // the money-moving Cloud Functions refuse to run. Emails in `exemptEmails`
  // pass through untouched so the team can verify before reopening.
  // The admin dashboard itself is never gated — that is what turns this off.

  getMaintenanceConfig() {
    return this.afs.collection('/config').doc('maintenance').valueChanges();
  }

  updateMaintenanceConfig(cfg: {
    isActive: boolean;
    titleFr: string;
    titleEn: string;
    messageFr: string;
    messageEn: string;
    exemptEmails: string[];
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('maintenance').set(cfg, { merge: true });
  }

  // ── Operating company (config/company) ────────────────────────────────────
  // The legal entity and its FINTRAC registration number, shown in the
  // investor dossier (/dossier, /dossier/plan). The dossier function fills them
  // in at serve time, so a change here reaches the documents within a minute
  // without redeploying anything.

  getCompanyConfig() {
    return this.afs.collection('/config').doc('company').valueChanges();
  }

  updateCompanyConfig(cfg: {
    legalName: string;
    fintracNumber: string;
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('company').set(cfg, { merge: true });
  }

  // ── Privacy policy (config/privacyPolicy) ─────────────────────────────────
  // The policy the mobile app renders. Sections are an ordered array because
  // order IS the data: `set(..., {merge:true})` replaces an array field
  // wholesale, so deleting and reordering just work — with a map, a removed
  // section's key would survive the merge forever.
  //
  // `version` is what drives re-acceptance in the app. It is NOT bumped on
  // every save: only when the admin explicitly publishes, so that fixing a typo
  // does not prompt every customer.

  getPrivacyPolicy() {
    return this.afs.collection('/config').doc('privacyPolicy').valueChanges();
  }

  updatePrivacyPolicy(cfg: {
    version: number;
    versionPublishedAt?: string;
    effectiveDateFr: string;
    effectiveDateEn: string;
    introFr: string;
    introEn: string;
    sections: PrivacySection[];
    contactTitleFr: string;
    contactTitleEn: string;
    contactBodyFr: string;
    contactBodyEn: string;
    contactEmail: string;
    lastUpdatedFr: string;
    lastUpdatedEn: string;
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('privacyPolicy').set(cfg, { merge: true });
  }

  // ── Transaction limits (config/limits) ────────────────────────────────────
  // Per-currency thresholds, because the same number means very different money
  // on each side of the CAD↔XAF corridor. Above `reviewAbove` a transaction
  // needs a compliance packet and admin approval; above `blockAbove` it is
  // refused. 0 (or absent) disables a threshold.

  getLimits() {
    return this.afs.collection('/config').doc('limits').valueChanges();
  }

  updateLimits(limits: {
    direct: { CAD: LimitPair; XAF: LimitPair };
    marketplace: { CAD: LimitPair; XAF: LimitPair };
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('limits').set(limits, { merge: true });
  }

  // ── Corridor switches (config/corridors) ──────────────────────────────────
  // Suspends ONE direction of the Canada↔Cameroon corridor without closing the
  // whole platform. Only `disabled: true` closes a direction — a missing doc or
  // field means open, so an unconfigured document changes nothing.
  // Direct transfers only: the marketplace crosses the same corridor and stays
  // open by design — unless you close it too, via config/marketplace below.

  getCorridors() {
    return this.afs.collection('/config').doc('corridors').valueChanges();
  }

  // Indexed rather than a fixed caToCm/cmToCa pair: with N countries the keys
  // are derived ('gbToCm', 'cmToFr', …), and hard-coding them here would mean
  // editing this file every time a country opens.
  //
  // `{ merge: true }` is what makes that safe — a dashboard build that only
  // knows some of the keys never clobbers the ones it has not heard of.
  updateCorridors(cfg: {
    [key: string]: CorridorSide | string[] | string;
  } & {
    exemptEmails: string[];
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('corridors').set(cfg, { merge: true });
  }

  // ── Countries (config/countries) ──────────────────────────────────────────
  // Which countries customers may transact with, per channel. Read by
  // CountryConfigService in the app and by assertCountryEnabled server-side.
  //
  // ⚠️ This document FAILS CLOSED, unlike every other switch here: a country
  // with no entry is UNAVAILABLE. Only Canada and Cameroon are exempt, because
  // they predate the document and have balances that could be stranded.
  // So writing `{ enabled: false }` and writing nothing at all mean the same
  // thing to the app — but the explicit false is auditable, and that is what
  // this dashboard always writes.

  getCountries() {
    return this.afs.collection('/config').doc('countries').valueChanges();
  }

  updateCountries(cfg: {
    [iso: string]: CountryFlags | string;
  } & { updatedAt: string; updatedBy: string }) {
    return this.afs.collection('/config').doc('countries').set(cfg, { merge: true });
  }

  // ── Bank deposit rails (config/depositRails) ──────────────────────────────
  // Where customers in a bank-rail country (UK, France, China) send their
  // transfer, and what reference to put on it. Canada uses Interac and Cameroon
  // Mobile Money; both have their own documents.
  //
  // ⚠️ `config/*` is world-readable (the pre-auth splash reads the maintenance
  // switch), so EVERYTHING here is public. Fine for a company receiving
  // account, whose details are shown on the deposit page anyway — but never put
  // an internal reference scheme or a key in here.

  getDepositRails() {
    return this.afs.collection('/config').doc('depositRails').valueChanges();
  }

  updateDepositRails(cfg: {
    [iso: string]: DepositRailConfig | string;
  } & { updatedAt: string; updatedBy: string }) {
    return this.afs.collection('/config').doc('depositRails').set(cfg, { merge: true });
  }

  // ── Marketplace kill switch (config/marketplace) ──────────────────────────
  // Closes the peer-to-peer marketplace (posts and offers) without touching
  // direct transfers. Only `disabled: true` closes it — a missing doc or field
  // means open. Two directions of targeting: `exemptEmails` keep access while
  // it is closed, `blockedEmails` lose access while it is open. An address in
  // both lists is blocked.
  //
  // Email lists MUST be trimmed + lowercased here: the app and
  // assertMarketplaceOpen in functions/index.js both match with a lowercase
  // includes().

  getMarketplaceConfig() {
    return this.afs.collection('/config').doc('marketplace').valueChanges();
  }

  updateMarketplaceConfig(cfg: {
    disabled: boolean;
    titleFr: string;
    titleEn: string;
    messageFr: string;
    messageEn: string;
    exemptEmails: string[];
    blockedEmails: string[];
    updatedAt: string;
    updatedBy: string;
  }) {
    return this.afs.collection('/config').doc('marketplace').set(cfg, { merge: true });
  }

  // ── Compliance reviews (complianceReviews) ────────────────────────────────
  // READ ONLY from here. The collection is `allow write: if false` in
  // firestore.rules — approving or rejecting goes through the
  // decideComplianceReview Cloud Function, never a direct write, because
  // approval is what actually settles the money.

  getAllComplianceReviews() {
    return this.afs.collection('/complianceReviews').snapshotChanges();
  }

  // add user
  addBeneficiary(beneficiary : Beneficiary) {
    beneficiary.id = this.afs.createId();
    return this.afs.collection('/beneficiaries').add(beneficiary);
  }
  
  // get all beneficiary
  getAllBeneficiary() {
    return this.afs.collection('/beneficiaries').snapshotChanges();
  }

    // delete user
  deleteBeneficiary(beneficiary : Beneficiary) {
      this.afs.doc('/beneficiaries/'+beneficiary.id).delete();
   }

  // update user
  updateBeneficiary(beneficiary : Beneficiary) {
    this.deleteBeneficiary(beneficiary);
    this.addBeneficiary(beneficiary);
  }

     // add transaction
  addTransaction(transaction : Transaction) {
      transaction.id = this.afs.createId();
      return this.afs.collection('/transactions').add(transaction);
  }
  _addTransaction(transaction : allTransaction) {
    transaction.id = this.afs.createId();
    return this.afs.collection('/allTransactions').add(transaction);
  }
  
     // get all transactions
  getAlltransactions() { 
      return this.afs.collection('/transactions').snapshotChanges();
  }
  // get all transactions
  _getAlltransactions() { 
        return this.afs.collection('/allTransactions').snapshotChanges();
  }

      // delete user
  deleteTransaction(transaction : Transaction) {
        this.afs.doc('/transactions/'+transaction.id).delete();
  }
      // delete user
  _deleteTransaction(transaction : allTransaction) {
        this.afs.doc('/allTransactions/'+transaction.id).delete();
  }

   // update user
   updateTransaction(transaction : Transaction) {
    this.deleteTransaction(transaction);
    this.afs.collection('/transactions').add(transaction);
  }
     // update user
  _updateTransaction(transaction : allTransaction) {
      this._deleteTransaction(transaction);
      this.afs.collection('/allTransactions').add(transaction);
  }

  // ── KYC VERIFICATIONS ──

  // get all kyc verifications
  getAllKycVerifications() {
    return this.afs.collection('/kyc_verifications').snapshotChanges();
  }

  // approve kyc verification
  approveKyc(docId: string) {
    return this.afs.collection('/kyc_verifications').doc(docId).update({
      status: 'verified',
      verifiedAt: new Date().toISOString()
    });
  }

  // set kyc verification back to pending
  setKycPending(docId: string) {
    return this.afs.collection('/kyc_verifications').doc(docId).update({
      status: 'pending',
      verifiedAt: '',
      rejectionReason: ''
    });
  }

  // reject kyc verification
  rejectKyc(docId: string, rejectionReason: string) {
    return this.afs.collection('/kyc_verifications').doc(docId).update({
      status: 'rejected',
      rejectionReason: rejectionReason
    });
  }

  // ── ADMIN MESSAGES ──

  // get all admin messages
  getAllAdminMessages() {
    return this.afs.collection('/admin_messages').snapshotChanges();
  }

  // add admin message
  addAdminMessage(msg: AdminMessage) {
    msg.createdAt = new Date().toISOString();
    msg.updatedAt = new Date().toISOString();
    return this.afs.collection('/admin_messages').add(msg);
  }

  // update admin message
  updateAdminMessage(id: string, msg: Partial<AdminMessage>) {
    msg.updatedAt = new Date().toISOString();
    return this.afs.collection('/admin_messages').doc(id).update(msg);
  }

  // delete admin message
  deleteAdminMessage(id: string) {
    return this.afs.collection('/admin_messages').doc(id).delete();
  }

  // ── Wallet Accounts ──────────────────────────────────────────────────────

  getAllWalletAccounts() {
    return this.afs.collection('/walletAccount').snapshotChanges();
  }

  updateWalletAmount(id: string, amount: number) {
    return this.afs.collection('/walletAccount').doc(id).update({ amount });
  }

  // Find the CAD wallet for a sender email by matching the account email
  // (usersEmail). Customers now register a phone number (interacPhone) rather
  // than an email, so there is no email-based Interac contact to match against.
  // Used by the Interac email "Mark Processed" flow to debit the sender's wallet.
  findWalletForInteracSender(senderEmail: string): Promise<WalletAccount | null> {
    const email = (senderEmail || '').trim().toLowerCase();
    if (!email) return Promise.resolve(null);

    return this.afs
      .collection<WalletAccount>('/walletAccount', ref =>
        ref.where('usersEmail', '==', email).where('currency', '==', 'CAD').limit(1)
      )
      .get().toPromise()
      .then(snap => {
        if (snap && !snap.empty) {
          const doc = snap.docs[0];
          return { ...(doc.data() as WalletAccount), id: doc.id };
        }
        return null;
      });
  }

  // ── Interac Emails ──────────────────────────────────────────────────────

  getAllInteracEmails() {
    return this.afs.collection('/interac_emails', ref => ref.orderBy('date', 'desc')).snapshotChanges();
  }

  updateInteracEmailStatus(id: string, status: 'new' | 'processed' | 'ignored') {
    const update: any = { status };
    if (status === 'processed') {
      update.processedAt = new Date().toISOString();
    }
    return this.afs.collection('/interac_emails').doc(id).update(update);
  }

  // ── Marketplace (posts + offres) ──────────────────────────────────────────
  // Lus par l'onglet Marketplace du dashboard pour croiser posts ↔ offres ↔
  // transactions et calculer les frais encaissés par la plateforme.

  getAllPosts() {
    return this.afs.collection('/posts').snapshotChanges();
  }

  getAllPropositions() {
    return this.afs.collection('/propositions').snapshotChanges();
  }

  // ── MoMo Deposits (Cameroun) ───────────────────────────────────────────────

  getAllMomoDeposits() {
    return this.afs.collection('/momo_deposits', ref => ref.orderBy('submittedAt', 'desc')).snapshotChanges();
  }

  updateMomoDepositStatus(id: string, status: 'pending' | 'approved' | 'rejected', rejectReason?: string) {
    const update: any = { status };
    if (status === 'approved') {
      update.approvedAt = new Date().toISOString();
    } else if (status === 'rejected') {
      update.rejectedAt = new Date().toISOString();
      if (rejectReason) update.rejectReason = rejectReason;
    }
    return this.afs.collection('/momo_deposits').doc(id).update(update);
  }

  // ── Bank deposits (Royaume-Uni, France, Chine) ────────────────────────────
  // Customer-declared bank transfers awaiting a manual credit. The same
  // review flow as MoMo, for the countries whose rail is neither Interac nor
  // Mobile Money.
  //
  // Read-only from here: approving CREDITS A WALLET, so it goes through the
  // creditBankDeposit Cloud Function — atomic, idempotent on the deposit doc,
  // amount taken from the stored row rather than the browser, and it writes a
  // moneyEvents audit line. A direct Firestore update would do none of that.

  getAllBankDeposits() {
    return this.afs
      .collection('/bank_deposits', ref => ref.orderBy('submittedAt', 'desc'))
      .snapshotChanges();
  }

  // ── Issue Report Emails ─────────────────────────────────────────────────

  getAllIssueReportEmails() {
    return this.afs.collection('/issue_report_emails', ref => ref.orderBy('date', 'desc')).snapshotChanges();
  }

  updateIssueReportStatus(id: string, status: 'new' | 'resolved' | 'ignored') {
    const update: any = { status };
    if (status === 'resolved') {
      update.resolvedAt = new Date().toISOString();
    }
    return this.afs.collection('/issue_report_emails').doc(id).update(update);
  }

  // ── Deposit Issue Reports (Interac / Mobile Money) ──────────────────────

  getAllDepositIssueReports() {
    return this.afs.collection('/deposit_issue_reports', ref => ref.orderBy('date', 'desc')).snapshotChanges();
  }

  updateDepositIssueReportStatus(id: string, status: 'new' | 'resolved' | 'ignored') {
    const update: any = { status };
    if (status === 'resolved') {
      update.resolvedAt = new Date().toISOString();
    }
    return this.afs.collection('/deposit_issue_reports').doc(id).update(update);
  }

  // ── Money events (audit log) ─────────────────────────────────────────────
  //
  // Query-first, NOT snapshotChanges() like every other tab — deliberately.
  // This collection is append-only and grows without bound: at 100k rows a
  // live full-collection stream means 100k reads on every dashboard open and a
  // browser that freezes. An operator investigating a past ticket does not need
  // live updates, so each of these is a bounded one-shot .get().

  /** The most recent events, all customers. The "what is happening now" view. */
  getRecentMoneyEvents(limitTo = 200) {
    return this.afs
      .collection('/moneyEvents', ref => ref.orderBy('createdAt', 'desc').limit(limitTo))
      .get();
  }

  /**
   * Bounded count of technical failures, for the dashboard queue.
   *
   * Equality + limit only — no orderBy, deliberately: adding one would demand a
   * composite index. The caller shows "50+" past the cap.
   */
  countMoneyEventErrors(limitTo = 51) {
    return this.afs
      .collection('/moneyEvents', ref => ref.where('outcome', '==', 'error').limit(limitTo))
      .get();
  }

  /** Everything that happened to one customer's money. */
  getMoneyEventsByEmail(email: string, limitTo = 200) {
    const key = (email || '').trim().toLowerCase();
    return this.afs
      .collection('/moneyEvents', ref =>
        ref.where('userEmail', '==', key).orderBy('createdAt', 'desc').limit(limitTo))
      .get();
  }

  /**
   * The complete story of ONE transaction, oldest first — the server attempt,
   * the observed balance move, and any failure the customer reported, all
   * carrying the same correlationId. The single most useful support action.
   */
  getMoneyEventsByCorrelation(correlationId: string) {
    const key = (correlationId || '').trim();
    return this.afs
      .collection('/moneyEvents', ref =>
        ref.where('correlationId', '==', key).orderBy('createdAt', 'asc').limit(200))
      .get();
  }

  // ── Push Notifications ──────────────────────────────────────────────────

  getAllNotifications() {
    return this.afs.collection('/notifications', ref => ref.orderBy('sentAt', 'desc')).snapshotChanges();
  }

  getAllUsers() {
    return this.afs.collection('/users').snapshotChanges();
  }

  // sync user's kycStatus in the users collection
  updateUserKycStatus(userEmail: string, status: string) {
    return this.afs.collection('/users', ref => ref.where('email', '==', userEmail).limit(1))
      .get().toPromise().then(snapshot => {
        if (!snapshot.empty) {
          snapshot.docs[0].ref.update({ kycStatus: status });
        }
      });
  }

  // look up the FCM token for a user by email. Returns null when missing.
  getUserFcmToken(userEmail: string): Promise<string | null> {
    return this.afs.collection('/users', ref => ref.where('email', '==', userEmail).limit(1))
      .get().toPromise().then(snapshot => {
        if (snapshot.empty) return null;
        const data: any = snapshot.docs[0].data();
        return data && data.fcmToken ? data.fcmToken : null;
      });
  }

  // ── Foreign Client Bill Payment Requests ───────────────────────────────
  // Requests from users (typically in Africa) asking an agent in Canada to
  // pay a bill on their behalf. User reimburses the amount plus a commission.

  getAllForeignBillPaymentRequests() {
    return this.afs.collection('/foreignBillPaymentRequests').snapshotChanges();
  }

  markForeignBillPaymentInProgress(id: string) {
    return this.afs.collection('/foreignBillPaymentRequests').doc(id).update({
      status: 'in_progress',
      pickedUpAt: new Date().toISOString(),
    });
  }

  markForeignBillPaymentPaid(id: string, receipt?: { proofUrl?: string; reference?: string; note?: string }) {
    const update: any = {
      status: 'paid',
      paidAt: new Date().toISOString(),
    };
    if (receipt) {
      if (receipt.proofUrl !== undefined) update.proofUrl = receipt.proofUrl;
      if (receipt.reference !== undefined) update.paymentReference = receipt.reference;
      if (receipt.note !== undefined) update.adminNote = receipt.note;
    }
    return this.afs.collection('/foreignBillPaymentRequests').doc(id).update(update);
  }

  markForeignBillPaymentReimbursed(id: string) {
    return this.afs.collection('/foreignBillPaymentRequests').doc(id).update({
      status: 'reimbursed',
      reimbursedAt: new Date().toISOString(),
    });
  }

  rejectForeignBillPayment(id: string, reason: string) {
    return this.afs.collection('/foreignBillPaymentRequests').doc(id).update({
      status: 'rejected',
      rejectionReason: reason,
      rejectedAt: new Date().toISOString(),
    });
  }

  setForeignBillUserMessage(id: string, messageEn: string, messageFr: string) {
    return this.afs.collection('/foreignBillPaymentRequests').doc(id).update({
      userMessageEn: messageEn,
      userMessageFr: messageFr,
      userMessageUpdatedAt: new Date().toISOString(),
      // Clear the deprecated single-language field so the user app no longer
      // falls back to stale content once the admin saves a bilingual message.
      userMessage: '',
    });
  }

  // ── Marketplace Posts ───────────────────────────────────────────────────

  // Fetch a single Post by document id. Returns null when not found.
  // Used by the admin transactions grid to show the Post + Proposition that
  // spawned a marketplace transaction pair.
  getPostById(postId: string): Promise<Post | null> {
    return this.afs.collection('/posts').doc(postId).get().toPromise().then(snap => {
      if (!snap.exists) return null;
      const data: any = snap.data();
      return { ...data, docId: snap.id } as Post;
    });
  }

}
